import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ELEMENT_TYPES, getConnectionPoints } from '../types/elements';
import ArchiMateElementRenderer from './ArchiMateElementRenderer';
import TextEditor from './TextEditor';
import { ShapeRenderer } from './renderers/ShapeRenderer';
import { ArrowRenderer } from './renderers/ArrowRenderer';
import { CanvasElement, ElementRendererProps, RendererSharedProps } from '../types/elementRenderer';
import { ShapeIcon, getGroupForType, ShapeEntry } from '../types/shapeGroups';

const FALLBACK_PICKER_SHAPES: ShapeEntry[] = [
  { type: 'rectangle', label: 'Прямоугольник' },
  { type: 'circle', label: 'Круг' },
];

const connectionPointStyles = {
  default: { fill: '#007ACC', stroke: '#ffffff', strokeWidth: 2, radius: 5, opacity: 0.7 },
  hover:   { fill: '#0056b3', stroke: '#ffffff', strokeWidth: 3, radius: 7, opacity: 1 },
};

const getMouseWorldFromSvg = (evt: MouseEvent, svgEl: SVGSVGElement, zoom: number) => {
  const rect = svgEl.getBoundingClientRect();
  return { x: (evt.clientX - rect.left) / zoom, y: (evt.clientY - rect.top) / zoom };
};

const ElementRenderer: React.FC<ElementRendererProps> = ({
  element,
  onSelect,
  onDrag,
  onArrowEdit,
  onStartArrowFromConnection,
  showConnectionPoints,
  onDoubleClick,
  onSelectMultiple,
  onDragMultiple,
  editingTextElement,
  onTextEditComplete,
  onTextEditCancel,
  onArrowCapsChange,
  zoom = 1,
  onContextMenu,
  onArrowPathPointDrag,
  onArrowAddPoint,
  onResizeStart,
  onResize,
  onHoverStart,
  onHoverEnd,
  isDraggingElement = false,
  onCreateConnectedElement,
  onConnectorDoubleClick,
}) => {
  const [hoveredConnectionPoint, setHoveredConnectionPoint] = useState<string | null>(null);
  const [pickerSide, setPickerSide] = useState<string | null>(null);
  const hidePickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (hidePickerTimerRef.current) clearTimeout(hidePickerTimerRef.current); }, []);

  // ─── Hover ──────────────────────────────────────────────────────────────
  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    const hasCode = !!(element.systemCode || element.parentSystemCode || element.code);
    if (onHoverStart && hasCode) {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      onHoverStart(element, rect.right, rect.top);
    }
  }, [element, onHoverStart]);

  const handleMouseLeave = useCallback(() => {
    const hasCode = !!(element.systemCode || element.parentSystemCode || element.code);
    if (onHoverEnd && hasCode) onHoverEnd();
  }, [element, onHoverEnd]);

  // ─── Generic shape mouse-down ────────────────────────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelectMultiple) onSelectMultiple(element.id, e); else onSelect?.(element.id);
    const svgEl = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
    if (!svgEl) return;
    const start = getMouseWorldFromSvg(e.nativeEvent, svgEl, zoom);
    const offsetX = start.x - element.x;
    const offsetY = start.y - element.y;
    let dragStarted = false;

    const onMove = (ev: MouseEvent) => {
      if (!dragStarted) dragStarted = true;
      const cur = getMouseWorldFromSvg(ev, svgEl, zoom);
      const newX = cur.x - offsetX;
      const newY = cur.y - offsetY;
      if (onDragMultiple) onDragMultiple(element.id, newX, newY, ev as unknown as React.MouseEvent);
      else onDrag?.(element.id, newX, newY);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [element, zoom, onSelect, onSelectMultiple, onDrag, onDragMultiple]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDoubleClick?.(element.id);
  }, [element.id, onDoubleClick]);

  // ─── Arrow endpoint drag ─────────────────────────────────────────────────
  const handleArrowPointMouseDown = useCallback((e: React.MouseEvent, isStart: boolean) => {
    e.stopPropagation();
    onSelect?.(element.id);
    const svgEl = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
    if (!svgEl) return;
    const move = (ev: MouseEvent) => {
      const { x, y } = getMouseWorldFromSvg(ev, svgEl, zoom);
      onArrowEdit?.(element.id, isStart, x, y);
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }, [element.id, zoom, onSelect, onArrowEdit]);

  // ─── Arrow body / path-segment drag ─────────────────────────────────────
  const handleArrowMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    // Alt+click → add intermediate path point
    if (e.altKey) {
      const svg = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const px = (e.clientX - rect.left) / zoom;
      const py = (e.clientY - rect.top) / zoom;
      const segs = element.pathPoints as Array<{ x: number; y: number }> | undefined;
      if (segs && segs.length > 1) {
        const distToSeg = (x: number, y: number, x1: number, y1: number, x2: number, y2: number) => {
          const A = x - x1; const B = y - y1; const C = x2 - x1; const D = y2 - y1;
          const dot = A * C + B * D; const lenSq = C * C + D * D;
          let t = lenSq ? dot / lenSq : 0;
          t = Math.max(0, Math.min(1, t));
          return { d: Math.hypot(x - (x1 + t * C), y - (y1 + t * D)), projX: x1 + t * C, projY: y1 + t * D };
        };
        let best = { idx: 0, d: Infinity, px, py };
        for (let i = 0; i < segs.length - 1; i++) {
          const res = distToSeg(px, py, segs[i].x, segs[i].y, segs[i + 1].x, segs[i + 1].y);
          if (res.d < best.d) best = { idx: i, d: res.d, px: res.projX, py: res.projY };
        }
        onArrowAddPoint?.(element.id, best.px, best.py, best.idx);
      } else {
        onArrowAddPoint?.(element.id, px, py, 0);
      }
      return;
    }

    if (onSelectMultiple) onSelectMultiple(element.id, e); else onSelect?.(element.id);

    const svg = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = (e.clientX - rect.left) / zoom;
    const py = (e.clientY - rect.top) / zoom;

    // If click is near an endpoint, delegate to endpoint drag
    const dist = (x1: number, y1: number, x2: number, y2: number) => Math.hypot(x1 - x2, y1 - y2);
    const dStart = dist(px, py, element.x, element.y);
    const dEnd = dist(px, py, element.x2 ?? element.x, element.y2 ?? element.y);
    const threshold = Math.max(14, (element.strokeWidth || 2) * 3);
    if (dStart <= threshold || dEnd <= threshold) {
      handleArrowPointMouseDown(e, dStart <= dEnd);
      return;
    }

    // Otherwise drag the whole arrow
    const start = { x: px, y: py };
    const offsetX = start.x - element.x;
    const offsetY = start.y - element.y;
    const move = (moveEvt: MouseEvent) => {
      const curRect = svg.getBoundingClientRect();
      const mx = (moveEvt.clientX - curRect.left) / zoom;
      const my = (moveEvt.clientY - curRect.top) / zoom;
      const newX = mx - offsetX;
      const newY = my - offsetY;
      if (onDragMultiple) onDragMultiple(element.id, newX, newY, moveEvt as unknown as React.MouseEvent);
      else onDrag?.(element.id, newX, newY);
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }, [element, zoom, onSelect, onSelectMultiple, onDrag, onDragMultiple, onArrowAddPoint, handleArrowPointMouseDown]);

  // ─── Connection points ───────────────────────────────────────────────────
  const handleConnectionPointMouseDown = useCallback((e: React.MouseEvent, side: string, point: { x: number; y: number }) => {
    e.stopPropagation();
    onStartArrowFromConnection?.(element.id, side, point);
  }, [element.id, onStartArrowFromConnection]);

  const connectionPointsData = useMemo(() => {
    if (isDraggingElement || !showConnectionPoints || !element.selected ||
        element.type === ELEMENT_TYPES.ARROW || element.type === ELEMENT_TYPES.TEXT) {
      return null;
    }
    return getConnectionPoints(element);
  }, [
    isDraggingElement, showConnectionPoints, element.selected, element.type,
    element.x, element.y, element.width, element.height, element.radius,
  ]);

  const renderConnectionPoints = () => {
    if (!connectionPointsData) return null;
    return Object.entries(connectionPointsData as Record<string, { x: number; y: number }>).map(([side, point]) => {
      const isHovered = hoveredConnectionPoint === side;
      const style = isHovered ? connectionPointStyles.hover : connectionPointStyles.default;
      const scaledRadius = style.radius / zoom;
      const scaledStrokeWidth = style.strokeWidth / zoom;
      const isPickerVisible = pickerSide === side;

      return (
        <g key={side}>
          {/* Wide transparent hit area */}
          <circle cx={point.x} cy={point.y} r={scaledRadius * 2.5} fill="transparent"
            style={{ cursor: 'crosshair' }}
            onMouseDown={(e) => handleConnectionPointMouseDown(e, side, point)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onConnectorDoubleClick?.(element.id, side, point);
            }}
            onMouseEnter={() => {
              setHoveredConnectionPoint(side);
              if (hidePickerTimerRef.current) clearTimeout(hidePickerTimerRef.current);
              setPickerSide(side);
            }}
            onMouseLeave={() => {
              setHoveredConnectionPoint(null);
              hidePickerTimerRef.current = setTimeout(() => setPickerSide(null), 150);
            }}
          />
          {/* Visible dot */}
          <circle cx={point.x} cy={point.y} r={scaledRadius}
            fill={style.fill} stroke={style.stroke} strokeWidth={scaledStrokeWidth}
            opacity={style.opacity}
            style={{ cursor: 'crosshair', transition: 'opacity 0.15s ease-out', pointerEvents: 'none' }}
          />
          {/* Shape picker popup */}
          {isPickerVisible && (() => {
            const pickerShapes = getGroupForType(element.type) ?? FALLBACK_PICKER_SHAPES;
            const cols = Math.min(pickerShapes.length, 3);
            const rows = Math.ceil(pickerShapes.length / cols);
            const itemSize = 26;
            const gap = 3;
            const pad = 6;
            const pxW = cols * itemSize + (cols - 1) * gap + pad * 2;
            const pxH = rows * itemSize + (rows - 1) * gap + pad * 2;
            return (
              <foreignObject
                x={point.x + 10 / zoom}
                y={point.y - pxH / 2 / zoom}
                width={pxW / zoom}
                height={pxH / zoom}
                style={{ overflow: 'visible' }}
                onMouseEnter={() => {
                  if (hidePickerTimerRef.current) clearTimeout(hidePickerTimerRef.current);
                }}
                onMouseLeave={() => {
                  hidePickerTimerRef.current = setTimeout(() => setPickerSide(null), 150);
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${cols}, ${itemSize}px)`,
                    gap: `${gap}px`,
                    background: 'white',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    padding: `${pad}px`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                  }}
                >
                  {pickerShapes.map(shape => (
                    <div
                      key={shape.type}
                      title={shape.label}
                      style={{
                        width: itemSize, height: itemSize,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 3,
                        background: 'transparent',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#f0f4f8'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setPickerSide(null);
                        onCreateConnectedElement?.(element.id, side, point, shape.type);
                      }}
                    >
                      <ShapeIcon type={shape.type} color="#333" />
                    </div>
                  ))}
                </div>
              </foreignObject>
            );
          })()}
        </g>
      );
    });
  };

  // ─── Resize handles ──────────────────────────────────────────────────────
  const startResize = (handle: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onResize) return;
    onResizeStart?.(element.id);
    const svgEl = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
    if (!svgEl) return;
    const start = getMouseWorldFromSvg(e.nativeEvent, svgEl, zoom);
    const init = { ...element };
    const minSize = 10;

    const onMove = (ev: MouseEvent) => {
      const cur = getMouseWorldFromSvg(ev, svgEl, zoom);
      const dx = cur.x - start.x;
      const dy = cur.y - start.y;

      if (element.type === ELEMENT_TYPES.CIRCLE && element.radius) {
        const cx0 = (init.x as number) + (init.radius as number);
        const cy0 = (init.y as number) + (init.radius as number);
        const r = Math.max(5, Math.hypot(cur.x - cx0, cur.y - cy0));
        onResize(element.id, { x: cx0 - r, y: cy0 - r, radius: r });
      } else {
        let { x, y, width, height } = init as { x: number; y: number; width: number; height: number };
        switch (handle) {
          case 'tl': x += dx; y += dy; width -= dx; height -= dy; break;
          case 'tr': y += dy; width += dx; height -= dy; break;
          case 'bl': x += dx; width -= dx; height += dy; break;
          case 'br': width += dx; height += dy; break;
          case 'l':  x += dx; width -= dx; break;
          case 'r':  width += dx; break;
          case 't':  y += dy; height -= dy; break;
          case 'b':  height += dy; break;
        }
        width = Math.max(minSize, width);
        height = Math.max(minSize, height);
        if (width === minSize && (handle === 'tl' || handle === 'bl' || handle === 'l'))
          x = (init.x as number) + ((init.width as number) - minSize);
        if (height === minSize && (handle === 'tl' || handle === 'tr' || handle === 't'))
          y = (init.y as number) + ((init.height as number) - minSize);
        onResize(element.id, { x, y, width, height });
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const renderResizeHandles = () => {
    if (!element.selected || element.type === ELEMENT_TYPES.ARROW || element.type === ELEMENT_TYPES.TEXT) return null;
    const size = 8 / zoom;
    const half = size / 2;
    const handles: React.ReactElement[] = [];

    if (element.type === ELEMENT_TYPES.CIRCLE && element.radius) {
      const { x, y, radius } = element;
      const cx = (x as number) + (radius as number);
      const cy = (y as number) + (radius as number);
      const r = radius as number;
      ([
        { key: 'r', x: cx + r, y: cy },
        { key: 'l', x: cx - r, y: cy },
        { key: 't', x: cx, y: cy - r },
        { key: 'b', x: cx, y: cy + r },
      ] as Array<{ key: string; x: number; y: number }>).forEach(p =>
        handles.push(
          <rect key={p.key} x={p.x - half} y={p.y - half} width={size} height={size}
            fill="#ffffff" stroke="#007ACC" strokeWidth={1 / zoom} style={{ cursor: 'grab' }}
            onMouseDown={startResize(p.key)} />
        )
      );
    } else {
      const { x, y, width, height } = element as { x: number; y: number; width: number; height: number };
      const midX = x + width / 2;
      const midY = y + height / 2;
      ([
        { key: 'tl', x, y, cursor: 'nw-resize' },
        { key: 'tr', x: x + width, y, cursor: 'ne-resize' },
        { key: 'bl', x, y: y + height, cursor: 'sw-resize' },
        { key: 'br', x: x + width, y: y + height, cursor: 'se-resize' },
        { key: 'l',  x, y: midY, cursor: 'w-resize' },
        { key: 'r',  x: x + width, y: midY, cursor: 'e-resize' },
        { key: 't',  x: midX, y, cursor: 'n-resize' },
        { key: 'b',  x: midX, y: y + height, cursor: 's-resize' },
      ] as Array<{ key: string; x: number; y: number; cursor: string }>).forEach(p =>
        handles.push(
          <rect key={p.key} x={p.x - half} y={p.y - half} width={size} height={size}
            fill="#ffffff" stroke="#007ACC" strokeWidth={1 / zoom} style={{ cursor: p.cursor }}
            onMouseDown={startResize(p.key)} />
        )
      );
    }
    return <g>{handles}</g>;
  };

  // ─── Label ───────────────────────────────────────────────────────────────
  const renderLabel = (): React.ReactNode => {
    if (!element.content) return null;
    let cx: number, cy: number;

    if (element.type === ELEMENT_TYPES.DATABASE || element.isDatabase) {
      const ry = Math.min((element.height as number) * 0.15, (element.width as number) * 0.2);
      cx = (element.x as number) + (element.width as number) / 2;
      cy = (element.y as number) + ry + ((element.height as number) - 2 * ry) / 2;
    } else if (element.radius) {
      cx = (element.x as number) + (element.radius as number);
      cy = (element.y as number) + (element.radius as number);
    } else if (element.width && element.height) {
      cx = (element.x as number) + (element.width as number) / 2;
      cy = (element.y as number) + (element.height as number) / 2;
    } else {
      return null;
    }

    const fs = (element.fontSize as number) || 16;
    return (
      <text x={cx} y={cy} fill={element.textColor || '#000'} fontSize={fs}
        fontFamily={(element.fontFamily as string) || 'Arial'}
        textAnchor="middle" dominantBaseline="middle"
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >
        {(element.content as string).split('\n').map((line, i, arr) => (
          <tspan key={`lbl-${i}-${line.slice(0, 20)}`} x={cx} dy={i === 0 ? -(arr.length - 1) * fs / 2 : fs}>{line}</tspan>
        ))}
      </text>
    );
  };

  // ─── Element dispatcher ──────────────────────────────────────────────────
  const renderElement = () => {
    // ArchiMate
    if (element.type === 'archimate') {
      return (
        <ArchiMateElementRenderer
          element={element}
          zoom={zoom}
          onSelect={onSelect}
          onDrag={onDrag}
          onDoubleClick={onDoubleClick}
          onStartArrowFromConnection={onStartArrowFromConnection}
          showConnectionPoints={showConnectionPoints}
          onContextMenu={onContextMenu}
          onSelectMultiple={onSelectMultiple}
          onDragMultiple={onDragMultiple}
          isDraggingElement={isDraggingElement}
        />
      );
    }

    const filterParts: string[] = [];
    if (element.registryError) filterParts.push('drop-shadow(0 0 8px #ef4444)');
    if (element.lintHighlight === 'warning') filterParts.push('drop-shadow(0 0 8px #f59e0b)');
    if (element.selected) filterParts.push('drop-shadow(0 0 8px #007ACC)');
    const filterStyle = filterParts.length ? filterParts.join(' ') : 'none';
    const commonStyle: React.CSSProperties = { cursor: 'move', filter: filterStyle };
    const dashArray = element.strokeDasharray as string | undefined;

    const shared: RendererSharedProps = {
      element,
      zoom,
      filterStyle,
      commonStyle,
      dashArray,
      onMouseDown: handleMouseDown,
      onDoubleClick: handleDoubleClick,
      onContextMenu: (e) => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e, element); },
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      renderLabel,
    };

    // Arrow
    if (element.type === ELEMENT_TYPES.ARROW) {
      return (
        <ArrowRenderer
          {...shared}
          onMouseDown={handleArrowMouseDown}
          onSelect={(id) => onSelect?.(id)}
          onArrowEdit={onArrowEdit}
          onArrowPathPointDrag={onArrowPathPointDrag}
          onArrowAddPoint={onArrowAddPoint}
        />
      );
    }

    // Text (inline — needs editingTextElement context)
    if (element.type === ELEMENT_TYPES.TEXT) {
      const isEditingThis = editingTextElement === element.id;
      const fs = (element.fontSize as number) || 16;
      return (
        <g>
          <text
            x={element.x} y={element.y}
            textAnchor={((element.textAnchor as string) || 'middle') as 'start' | 'middle' | 'end' | 'inherit'} dominantBaseline="middle"
            fill={element.fill as string}
            fontSize={fs}
            fontFamily={(element.fontFamily as string) || 'Arial'}
            style={{ cursor: 'move', userSelect: 'none', filter: filterStyle, visibility: isEditingThis ? 'hidden' : 'visible' }}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e, element); }}
          >
            {element.content && (element.content as string).split('\n').map((line, i, arr) => (
              <tspan key={`txt-${i}-${line.slice(0, 20)}`} x={element.x} dy={i === 0 ? -(arr.length - 1) * fs / 2 : fs}>{line}</tspan>
            ))}
          </text>
          {isEditingThis && (
            <TextEditor
              element={element}
              onComplete={onTextEditComplete as (id: string, payload: { content: string; fontFamily: string; fontSize: number }) => void}
              onCancel={onTextEditCancel as () => void}
            />
          )}
        </g>
      );
    }

    // All other shapes
    return <ShapeRenderer {...shared} />;
  };

  return (
    <>
      {/* oxlint-disable-next-line react/no-unstable-nested-components */}
      {renderElement()}
      {/* oxlint-disable-next-line react/no-unstable-nested-components */}
      {renderConnectionPoints()}
      {/* oxlint-disable-next-line react/no-unstable-nested-components */}
      {renderResizeHandles()}
    </>
  );
};

export default ElementRenderer;
